import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { RepresentativeAdminRow } from '../models/representative-admin'

export const REPRESENTATIVES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Representante',
  identification: 'DPI',
  position: 'Puesto',
  societyName: 'Sociedad',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function RepresentativeActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

/** DPI legible: `1943 64194 0101` (así se imprime en los documentos). */
export function formatIdentification(value: string): string {
  if (!/^\d{13}$/.test(value)) return value
  return `${value.slice(0, 4)} ${value.slice(4, 9)} ${value.slice(9)}`
}

export const representativesAdminColumns: ColumnDef<RepresentativeAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Representante" />
    ),
    cell: ({ row }) => (
      <span className="block max-w-[20rem] truncate font-medium" title={row.original.name}>
        {row.original.name}
      </span>
    )
  },
  {
    accessorKey: 'identification',
    header: ({ column }) => <DataTableColumnHeader column={column} name="DPI" sortable={false} />,
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatIdentification(row.original.identification)}
      </span>
    )
  },
  {
    accessorKey: 'position',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[200px]" column={column} name="Puesto" />
    ),
    cell: ({ row }) => (
      <span className="block max-w-[18rem] truncate" title={row.original.position}>
        {row.original.position}
      </span>
    )
  },
  {
    accessorKey: 'societyName',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Sociedad" />
    ),
    cell: ({ row }) =>
      row.original.societyName ? (
        <span className="block max-w-[18rem] truncate" title={row.original.societyName}>
          {row.original.societyName}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <RepresentativeActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
